import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { ChevronLeft, Download, UploadCloud, CheckCircle2, XCircle, SkipForward, RefreshCw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "vd-admin-token";

const AdminProductImport = () => {
  const [mode, setMode] = useState("upsert");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const onPick = (f) => {
    setErr(""); setReport(null);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) { setErr("Please choose a .csv file."); return; }
    setFile(f);
  };

  const onDownloadTemplate = async () => {
    // Use fetch so we can bearer-auth + grab the streaming response
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${BACKEND}/api/admin/products/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Could not download template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "valuedrive-products-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e.message || "Download failed");
    }
  };

  const onUpload = async () => {
    if (!file) return;
    setUploading(true); setErr(""); setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await adminApi.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-import-page">
      <Link to="/admin/products" className="inline-flex items-center gap-1 vd-muted text-sm mb-3" data-testid="import-back-link"><ChevronLeft size={14} /> Back to products</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div>
          <h1 style={{ fontSize: 28 }}>Bulk import products</h1>
          <p className="vd-muted">Upload a CSV to create or update many tyres at once.</p>
        </div>
        <button onClick={onDownloadTemplate} className="vd-btn-secondary" data-testid="import-download-template"><Download size={16} /> Download template</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 mt-6">
        <div className="vd-card p-6" data-testid="import-uploader-card">
          <p className="vd-heading font-semibold mb-3">1. Choose mode</p>
          <div className="flex flex-col gap-2 mb-5">
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer" style={{ borderColor: mode === "upsert" ? "var(--primary-red)" : "var(--border-color)" }}>
              <input type="radio" className="vd-checkbox mt-0.5" checked={mode === "upsert"} onChange={() => setMode("upsert")} data-testid="import-mode-upsert" />
              <div>
                <p className="vd-heading font-semibold text-sm">Upsert (recommended)</p>
                <p className="vd-muted text-xs">Rows that match an existing product by Brand + Model + Size are updated; the rest are created.</p>
              </div>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer" style={{ borderColor: mode === "create_only" ? "var(--primary-red)" : "var(--border-color)" }}>
              <input type="radio" className="vd-checkbox mt-0.5" checked={mode === "create_only"} onChange={() => setMode("create_only")} data-testid="import-mode-create" />
              <div>
                <p className="vd-heading font-semibold text-sm">Create only</p>
                <p className="vd-muted text-xs">Existing rows are skipped — useful when seeding a fresh catalogue.</p>
              </div>
            </label>
          </div>

          <p className="vd-heading font-semibold mb-3">2. Pick a CSV file</p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-md flex flex-col items-center justify-center vd-muted text-sm py-10"
            style={{ border: "1px dashed var(--border-color)", background: "var(--bg-secondary)" }}
            data-testid="import-file-button"
          >
            <UploadCloud size={28} color="var(--primary-red)" />
            <p className="vd-heading font-medium mt-2">{file ? file.name : "Click to choose a .csv"}</p>
            <p className="vd-muted text-xs mt-1">UTF-8 encoded · use “|” to separate multiple image URLs</p>
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => onPick(e.target.files?.[0])} data-testid="import-file-input" />

          {err && <p className="vd-card p-3 mt-4 text-sm" style={{ color: "var(--primary-red)" }} data-testid="import-error">{err}</p>}

          <button
            onClick={onUpload}
            disabled={!file || uploading}
            className="vd-btn-primary w-full justify-center mt-5"
            data-testid="import-upload-btn"
          >{uploading ? "Importing…" : (<><RefreshCw size={16} /> Import {file ? `“${file.name}”` : "CSV"}</>)}</button>
        </div>

        <div className="vd-card p-6 h-fit" data-testid="import-help-card">
          <p className="vd-heading font-semibold mb-3">CSV format</p>
          <p className="vd-muted text-sm mb-3">Headers (case-sensitive) — required columns marked with <span style={{ color: "var(--primary-red)" }}>*</span>:</p>
          <ul className="text-xs space-y-1 vd-body font-mono" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", padding: 12, borderRadius: 8 }}>
            <li>brand<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>model<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>size <span className="vd-muted">(e.g. 205/55 R16)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>category <span className="vd-muted">(Summer | Winter | All-Season)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>load_index<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>speed_rating<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>fuel_efficiency <span className="vd-muted">(A–G)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>wet_grip <span className="vd-muted">(A–G)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>noise_rating <span className="vd-muted">(dB)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>price<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>sale_price <span className="vd-muted">(optional)</span></li>
            <li>wholesale_cost <span className="vd-muted">(optional)</span></li>
            <li>stock_qty<span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>description <span className="vd-muted">(optional)</span></li>
            <li>images <span className="vd-muted">(1–5, separated by “|”)</span><span style={{ color: "var(--primary-red)" }}> *</span></li>
            <li>is_visible <span className="vd-muted">(true/false, default true)</span></li>
          </ul>
        </div>
      </div>

      {report && (
        <div className="vd-card p-6 mt-6" data-testid="import-report">
          <p className="vd-heading font-semibold mb-3">Import results</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <Stat label="Created" value={report.created} color="#00A35C" Icon={CheckCircle2} testid="report-created" />
            <Stat label="Updated" value={report.updated} color="#3D7AFF" Icon={RefreshCw} testid="report-updated" />
            <Stat label="Skipped" value={report.skipped} color="#B57E00" Icon={SkipForward} testid="report-skipped" />
            <Stat label="Failed"  value={report.failed}  color="var(--primary-red)" Icon={XCircle} testid="report-failed" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left vd-muted uppercase text-xs tracking-wider border-b" style={{ borderColor: "var(--border-color)" }}>
                <th className="px-2 py-2">Row</th><th>Brand</th><th>Model</th><th>Size</th><th>Status</th><th>Detail</th>
              </tr></thead>
              <tbody>
                {report.results.map((r) => (
                  <tr key={r.row} className="border-t" style={{ borderColor: "var(--border-color)" }} data-testid={`report-row-${r.row}`}>
                    <td className="px-2 py-2 vd-muted">{r.row}</td>
                    <td className="vd-heading">{r.brand || "—"}</td>
                    <td className="vd-heading">{r.model || "—"}</td>
                    <td className="vd-muted">{r.size || "—"}</td>
                    <td>
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="vd-muted text-xs">{r.error || r.reason || (r.id ? `id ${r.id.slice(0, 8)}…` : "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2 mt-5">
            <Link to="/admin/products" className="vd-btn-primary" data-testid="report-view-products">View products</Link>
            <button onClick={() => { setFile(null); setReport(null); }} className="vd-btn-secondary" data-testid="report-import-another">Import another file</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, Icon, testid }) => (
  <div className="vd-card p-4 flex items-center gap-3" data-testid={testid}>
    <span className="inline-flex items-center justify-center rounded-md" style={{ width: 36, height: 36, background: `${color}22`, color }}>
      <Icon size={18} />
    </span>
    <div>
      <p className="vd-muted text-xs uppercase tracking-wider">{label}</p>
      <p className="vd-heading font-extrabold" style={{ fontSize: 22 }}>{value}</p>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    created: { bg: "rgba(0,180,80,0.15)", fg: "#00A35C" },
    updated: { bg: "rgba(61,122,255,0.15)", fg: "#3D7AFF" },
    skipped: { bg: "rgba(255,179,11,0.18)", fg: "#B57E00" },
    error:   { bg: "rgba(230,0,0,0.15)", fg: "var(--primary-red)" },
  };
  const c = map[status] || { bg: "var(--bg-secondary)", fg: "var(--text-heading)" };
  return <span className="text-xs font-semibold rounded px-2 py-0.5" style={{ background: c.bg, color: c.fg }}>{status}</span>;
};

export default AdminProductImport;
